import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UnassignedTicketsService } from '../../data-services/unassigned-tickets.service';
import { CustomClaimService } from '../../services/custom-claim.service';

interface NavItem {
  label: string;
  icon: string;
  route: string;
  active?: boolean;
  badge?: boolean; // For notification indicators
  badgeCount?: number; // For numeric badge display
  comingSoon?: boolean; // For features not yet available
  externalUrl?: boolean; // For external links
  superAdminOnly?: boolean; // Only visible to super admins
}

interface NavSection {
  items: NavItem[];
  showDivider?: boolean; // Show divider after this section
  superAdminOnly?: boolean; // Only visible to super admins
}

/**
 * Sidebar Navigation Component - Admin Portal
 * Left-hand navigation with ADN logo and admin-specific menu items
 * Based on web portal design but tailored for internal staff operations
 */
@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss']
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() collapsed: boolean = false;
  @Output() toggleSidebar = new EventEmitter<void>();

  private routerSubscription: Subscription | null = null;

  // Track if user is super admin
  isSuperAdmin = false;

  // Navigation organized in sections for admin operations
  navSections: NavSection[] = [
    {
      items: [
        { label: 'Dashboard', icon: 'bi-speedometer2', route: '/dashboard' },
        { label: 'Users', icon: 'bi-people', route: '/users' },
        { label: 'Organizations', icon: 'bi-building', route: '/organizations' }
      ],
      showDivider: true
    },
    {
      items: [
        { label: 'Drone Inventory', icon: 'bi-box-seam', route: '/drones' },
        { label: 'Replacements', icon: 'bi-arrow-repeat', route: '/replacements', comingSoon: true },
        { label: 'Maintenance', icon: 'bi-tools', route: '/maintenance', comingSoon: true }
      ],
      showDivider: true
    },
    {
      items: [
        { label: 'Flight Records', icon: 'bi-journal-text', route: '/flight-records' },
        { label: 'DJI Flights', icon: 'bi-airplane', route: '/dji-flights' },
        { label: 'FAA Reports', icon: 'bi-file-earmark-text', route: '/faa-reports' },
        { label: 'State Reports', icon: 'bi-file-earmark-ruled', route: '/state-reports' },
        { label: 'Acreage Reports', icon: 'bi-map', route: '/acreage-reports' },
        { label: 'Chemical Reports', icon: 'bi-droplet', route: '/chemical-reports' },
        { label: 'Drone Reports', icon: 'bi-file-earmark-text', route: '/drone-reports'},
        { label: 'Safety Tests', icon: 'bi-clipboard-check', route: '/safety-tests' }
      ],
      showDivider: true
    },
    {
      items: [
        { label: 'Support Tickets', icon: 'bi-life-preserver', route: '/support', comingSoon: true },
        { label: 'Unassigned Tickets', icon: 'bi-ticket-perforated', route: '/support/unassigned-tickets', badgeCount: 0 },
        { label: 'Orders', icon: 'bi-cart3', route: '/orders', comingSoon: true },
        { label: 'Analytics', icon: 'bi-graph-up', route: '/analytics', comingSoon: true }
      ],
      showDivider: true
    },
    // Agremo section
    {
      items: [
        { label: 'Agremo Analyses', icon: 'bi-graph-up-arrow', route: '/agremo-analyses' },
        { label: 'Agremo Credits', icon: 'bi-credit-card-2-front', route: '/agremo-credits' }
      ],
      showDivider: true
    },
    // Marketplace section — payment approval queue, dispute resolution,
    // and (future) all-payments + payouts views. Labeled "Marketplace"
    // (the section covers approvals, disputes, and payouts — not just
    // payments). (2026-06-11 Chris feedback.)
    {
      items: [
        { label: 'Marketplace', icon: 'bi-shop', route: '/marketplace-payments' }
      ],
      showDivider: true
    },
    {
      items: [
        { label: 'Settings', icon: 'bi-gear', route: '/settings' },
        { label: 'Drone Catalog', icon: 'bi-collection', route: '/drone-catalog' },
        { label: 'Acre Advantage', icon: 'bi-droplet-half', route: '/chemicals' },
        { label: 'EPA Chemicals', icon: 'bi-shield-check', route: '/epa-chemicals' },
        { label: 'Sponsors', icon: 'bi-award', route: '/sponsors' },
        { label: 'System Logs', icon: 'bi-list-ul', route: '/logs', comingSoon: true }
      ],
      showDivider: true
    },
    // Developer Tools section - Super Admin only
    {
      items: [
        { label: 'Reset Integration', icon: 'bi-arrow-clockwise', route: '/system/reset-integration', superAdminOnly: true }
      ],
      showDivider: false,
      superAdminOnly: true
    }
  ];

  constructor(
    private router: Router,
    private unassignedTicketsService: UnassignedTicketsService,
    private customClaimService: CustomClaimService
  ) {}

  ngOnInit(): void {
    // Check if user is super admin
    this.isSuperAdmin = this.customClaimService.isSuperAdmin();

    // Set initial active state
    this.updateActiveRoute();

    // Load unassigned ticket count for badge
    this.loadUnassignedCount();

    // Subscribe to router events to update active state on navigation
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateActiveRoute();
      });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Load unassigned ticket count and update the nav badge
   */
  private async loadUnassignedCount(): Promise<void> {
    try {
      const count = await this.unassignedTicketsService.getUnassignedCount();
      const unassignedItem = this.navSections[3]?.items.find(
        item => item.route === '/support/unassigned-tickets'
      );
      if (unassignedItem) {
        unassignedItem.badgeCount = count;
        unassignedItem.badge = count > 0;
      }
    } catch (error) {
      // Silently fail - badge just won't show
      console.error('Failed to load unassigned ticket count for sidebar:', error);
    }
  }

  /**
   * Update active state based on current route
   */
  private updateActiveRoute(): void {
    const currentRoute = this.router.url;
    this.navSections.forEach(section => {
      section.items.forEach(item => {
        item.active = currentRoute.startsWith(item.route);
      });
    });
  }

  /**
   * Get visible nav sections based on user permissions
   */
  getVisibleSections(): NavSection[] {
    return this.navSections.filter(section => {
      // Filter out super admin only sections if user is not super admin
      if (section.superAdminOnly && !this.isSuperAdmin) {
        return false;
      }
      return true;
    });
  }

  /**
   * Get visible items in a section based on user permissions
   */
  getVisibleItems(section: NavSection): NavItem[] {
    return section.items.filter(item => {
      // Filter out super admin only items if user is not super admin
      if (item.superAdminOnly && !this.isSuperAdmin) {
        return false;
      }
      return true;
    });
  }

  /**
   * Navigate to route (only if not coming soon)
   */
  navigateTo(item: NavItem): void {
    if (item.comingSoon) {
      return; // Don't navigate to coming soon features
    }

    if (item.externalUrl) {
      window.open(item.route, '_blank');
      return;
    }

    this.router.navigate([item.route]);

    // Close sidebar on mobile after navigation
    if (window.innerWidth <= 768) {
      this.onToggleSidebar();
    }
  }

  /**
   * Emit toggle sidebar event
   */
  onToggleSidebar(): void {
    this.toggleSidebar.emit();
  }

  /**
   * Close sidebar (for mobile close button)
   */
  closeSidebar(): void {
    this.onToggleSidebar();
  }
}
